/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";

import {
  AutoSaveField as Field,
  SelectAutocomplete,
  EmiratesID,
  Input,
  Checkbox,
  DatePicker
} from "../../../../components/Form";
import { UAE } from "../../../../constants";
import { SPOUSE_VISA_SPONSOR_TYPE } from "./constants";
import { Card } from "../../../../components/v2/Card/Card";

/**
 * CountryOfResidence component for rendering the country of residence form fields
 *
 * @param {Object} props - Component props
 */
export const CountryOfResidence = ({
  index,
  values,
  setFieldValue,
  errors,
  isShareholderACompany,
  tomorrow,
  yearOfBirth,
  isUAEOrGCC,
  pathToEidNumber,
  changeDateProspectHandler
}) => {
  return (
    <Card title="Country of residence and visa">
      <Field
        name="residenceCountry"
        path={`prospect.signatoryInfo[${index}].residenceCountry`}
        label="Country of residence"
        component={SelectAutocomplete}
        disabled={isShareholderACompany}
        datalistId="country"
        shrink
        tabIndex="0"
        changeProspect={(prospect, value) => ({
          ...prospect,
          [`prospect.signatoryInfo[${index}].kycDetails.addressInfo[0].addressDetails.country`]: value,
          [`prospect.signatoryInfo[${index}].residenceCountry`]: value
        })}
        onChange={value => {
          setFieldValue("residenceCountry", value);
          if (value !== UAE) {
            setFieldValue("eidNumber", "");
            setFieldValue("eidExpiryDate", "");
          }
        }}
      />
      {values.residenceCountry === UAE && (
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid
            item
            sm={6}
            xs={12}
            sx={{
              pt: "0px !important"
            }}
          >
            <Field
              name="eidNumber"
              path={pathToEidNumber}
              disabled={isShareholderACompany || values.residenceCountry !== UAE}
              label="Emirates ID"
              placeholder="1950-1234567-8"
              component={EmiratesID}
              changeProspect={(prospect, value) => ({
                ...prospect,
                [pathToEidNumber]: value.replace(/-/g, "")
              })}
              contextualHelpText={
                <>
                  If Emirates ID contains hyphen (-), spaces or any other special character please
                  enter only alphabets and numbers. Enter from year of birth.
                  <br />
                  Example
                  <br />
                  784-1950-1234567-8 to be entered as 195012345678
                </>
              }
            />
          </Grid>
          <Grid
            item
            sm={6}
            xs={12}
            sx={{
              pt: {
                xs: "16px !important",
                sm: "0px !important"
              }
            }}
          >
            <Field
              name="eidExpiryDate"
              path={`prospect.signatoryInfo[${index}].emirateIdDetails.eidExpiryDate`}
              disabled={isShareholderACompany || values.residenceCountry !== UAE}
              component={DatePicker}
              minDate={tomorrow}
              label="Emirates ID Expiry Date"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
              changeProspect={changeDateProspectHandler}
              disableFuture={false}
            />
          </Grid>
        </Grid>
      )}
      {values.residenceCountry === UAE && (
        <Field
          name="visaSponser"
          path={`prospect.signatoryInfo[${index}].visaSponser`}
          label="Visa Sponsor"
          datalistId="visaSponsor"
          disabled={isShareholderACompany || isUAEOrGCC}
          component={SelectAutocomplete}
          InputProps={{
            inputProps: { maxLength: 30, tabIndex: 0 }
          }}
        />
      )}
      {values.visaSponser === SPOUSE_VISA_SPONSOR_TYPE && (
        <Field
          name="visaSponsorPassportNo"
          label="Passport no for Visa Sponsor"
          disabled={isShareholderACompany}
          path={`prospect.signatoryInfo[${index}].visaSponsorPassportNo`}
          component={Input}
          InputProps={{
            inputProps: {
              tabIndex: 0
            }
          }}
        />
      )}
      <Field
        name="usRelatives"
        path={`prospect.signatoryInfo[${index}].isUSrelation`}
        label="I am US National /US Green Card holder /Resident or Tax Resident of US."
        component={Checkbox}
        disabled={isShareholderACompany}
        inputProps={{ tabIndex: 0 }}
      />
    </Card>
  );
};

CountryOfResidence.propTypes = {
  /**
   * Index of the stakeholder in the ordering
   */
  index: PropTypes.number.isRequired,
  /**
   * Form values object from Formik
   */
  values: PropTypes.object.isRequired,
  /**
   * Function to set field value in Formik
   */
  setFieldValue: PropTypes.func.isRequired,
  /**
   * Form errors object from Formik
   */
  errors: PropTypes.object,
  /**
   * Flag indicating if the stakeholder is a company
   */
  isShareholderACompany: PropTypes.bool.isRequired,
  /**
   * Minimum date for expiry date
   */
  tomorrow: PropTypes.instanceOf(Date).isRequired,
  /**
   * Year of birth for validation
   */
  yearOfBirth: PropTypes.number.isRequired,
  /**
   * Flag indicating if stakeholder has UAE or GCC nationality
   */
  isUAEOrGCC: PropTypes.bool.isRequired,
  /**
   * Path to Emirates ID field
   */
  pathToEidNumber: PropTypes.string.isRequired,
  /**
   * Function to handle date changes
   */
  changeDateProspectHandler: PropTypes.func.isRequired
};
